import type { MeetingRole, MeetingView } from "../../../../spurt/meetings";

/** Non-hosts may join this long before the start (the server's MEET_EARLY_JOIN_MIN default). */
export const EARLY_JOIN_MS = 15 * 60_000;

export const isHostRole = (r: MeetingRole | undefined): boolean => r === "organizer" || r === "co-organizer";

const dayFmt = (tz?: string) => new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short", ...(tz ? { timeZone: tz } : {}) });
const timeFmt = (tz?: string) => new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", ...(tz ? { timeZone: tz } : {}) });

const safeTz = (tz?: string): string | undefined => {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
};

export const fmtDay = (ms: number, tz?: string): string => dayFmt(safeTz(tz)).format(ms);
export const fmtClockTime = (ms: number, tz?: string): string => timeFmt(safeTz(tz)).format(ms);

/** "Tue 3 Nov, 9:00 AM - 10:00 AM" (or both dates when it crosses midnight). */
export const fmtWhen = (startMs: number, endMs: number, tz?: string): string => {
  const z = safeTz(tz);
  const sd = fmtDay(startMs, z);
  const ed = fmtDay(endMs, z);
  return sd === ed ? `${sd}, ${fmtClockTime(startMs, z)} - ${fmtClockTime(endMs, z)}` : `${sd}, ${fmtClockTime(startMs, z)} - ${ed}, ${fmtClockTime(endMs, z)}`;
};

/** "today at 9:00 AM" / "Tue 3 Nov at 9:00 AM": for sentences like "opens at ...". */
export const fmtMoment = (ms: number, now: number = Date.now()): string => {
  const same = new Date(ms).toDateString() === new Date(now).toDateString();
  return same ? `today at ${fmtClockTime(ms)}` : `${fmtDay(ms)} at ${fmtClockTime(ms)}`;
};

export const fmtDuration = (ms: number): string => {
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
};

export type MeetingBadge = "live" | "soon" | "ended" | "scheduled" | "canceled";

export const badgeOf = (m: Pick<MeetingView, "status" | "startMs" | "endMs">, now: number): MeetingBadge => {
  if (m.status === "live") return "live";
  if (m.status === "ended") return "ended";
  if (m.status === "canceled") return "canceled";
  if (m.endMs < now) return "ended";
  if (m.startMs - now <= EARLY_JOIN_MS) return "soon";
  return "scheduled";
};

export const BADGE_TEXT: Record<MeetingBadge, string> = { live: "Live", soon: "Starting soon", ended: "Ended", scheduled: "Scheduled", canceled: "Cancelled" };

/** Copy text to the clipboard; false when the browser refuses. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = typeof document.execCommand === "function" ? document.execCommand("copy") : false;
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Hand the person a file built in the browser. */
export function saveBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const saveText = (filename: string, text: string, mime = "text/plain"): void => saveBlob(filename, new Blob([text], { type: mime + ";charset=utf-8" }));

export const safeFileName = (s: string): string => s.replace(/[^A-Za-z0-9._ -]+/g, "").trim().replace(/\s+/g, "-").slice(0, 80) || "meeting";
